import React, { useEffect, useState } from 'react';
import './CertificateDetail.css';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import DownloadIcon from '@mui/icons-material/Download';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import TagIcon from '@mui/icons-material/Tag';
import { Snackbar, Alert } from '@mui/material';
import { motion } from 'framer-motion';
import { getCertificateDetail, downloadCertificatePdf } from '../../../api/certification';

const BADGE_COLORS = {
  bronze: '#CD7F32',
  silver: '#A8A9AD',
  gold: '#D4AF37',
  platinum: '#6E7F80',
};

function getBadgeColor(level) {
  return BADGE_COLORS[String(level || '').toLowerCase()] || '#4be3cf';
}

function getBadgeIconPath(badgeType) {
  const type = String(badgeType || '').toLowerCase();
  if (type === 'bronze') return '/Assets/Certification/bronze.svg';
  if (type === 'silver') return '/Assets/Certification/silver.svg';
  if (type === 'gold') return '/Assets/Certification/gold.svg';
  if (type === 'platinum') return '/Assets/Certification/platinum.svg';
  return '/Assets/Certification/bronze.svg';
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

export default function CertificateDetail() {
  const { userCertificateId } = useParams();
  const navigate = useNavigate();

  const [cert, setCert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'error') =>
    setSnackbar({ open: true, message, severity });

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await getCertificateDetail(userCertificateId);
        setCert(res?.data ?? res);
      } catch (e) {
        const status = e?.response?.status;
        if (status === 401) navigate('/signin');
        else if (status === 404) showSnackbar('Certificate not found.');
        else showSnackbar(e?.response?.data?.message || 'Failed to load certificate.');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [userCertificateId, navigate]);

  const handleDownload = async () => {
    if (downloading) return;
    const filename = `${cert?.certificateNumber ?? cert?.certNumber ?? userCertificateId}.pdf`;
    setDownloading(true);
    try {
      const blob = await downloadCertificatePdf(userCertificateId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      showSnackbar('Certificate downloaded!', 'success');
    } catch (e) {
      showSnackbar(e?.message || 'Download failed. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  // ── Loading skeleton ──
  if (loading) {
    return (
      <div className="cd-page">
        <div className="cd-container">
          <div className="cd-skel-back" />
          <div className="cd-hero cd-hero-skeleton">
            <div className="cd-skeleton cd-skel-badge-lg" />
            <div className="cd-skel-hero-text">
              <div className="cd-skeleton cd-skel-title-lg" />
              <div className="cd-skeleton cd-skel-sub" />
              <div className="cd-skeleton cd-skel-sub cd-skel-sub-short" />
            </div>
          </div>
          <div className="cd-skeleton cd-skel-info" />
          <div className="cd-skeleton cd-skel-btn" />
        </div>
      </div>
    );
  }

  if (!cert) return null;

  const badgeColor = getBadgeColor(cert.badgeType ?? cert.badgeLevel ?? cert.level);
  const isExpired = cert.isExpired ?? false;

  return (
    <div className="cd-page">
      <div className="cd-container">
        {/* Back */}
        <button className="cd-back" onClick={() => navigate('/my-certificates')}>
          <ArrowBackIcon style={{ fontSize: 16 }} /> My Certificates
        </button>

        {/* Hero card */}
        <motion.div
          className="cd-hero"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
          style={{ borderColor: `${badgeColor}33` }}
        >
          {/* Badge */}
          <div
            className="cd-badge-icon"
            style={{ background: `${badgeColor}1a`, border: `2px solid ${badgeColor}55` }}
          >
            <img
              src={getBadgeIconPath(cert.badgeType ?? cert.badgeLevel ?? cert.level)}
              alt={`${cert.badgeType ?? 'Certificate'} Badge`}
              style={{ width: '56px', height: '56px', objectFit: 'contain' }}
            />
          </div>

          {/* Hero text */}
          <div className="cd-hero-body">
            <div className="cd-hero-name-row">
              <h1 className="cd-cert-name">{cert.certificationName ?? cert.name}</h1>
              {isExpired ? (
                <span className="cd-expired-pill">Expired</span>
              ) : (
                <span className="cd-active-pill" style={{ background: 'rgba(75, 227, 207, 0.12)', color: '#4be3cf', fontSize: '11px', fontWeight: 700, borderRadius: '20px', padding: '3px 10px', marginLeft: '10px', alignSelf: 'center' }}>Active</span>
              )}
            </div>
            {(cert.badgeType ?? cert.badgeLevel ?? cert.level) && (
              <span className="cd-badge-level" style={{ color: badgeColor }}>
                {(cert.badgeType ?? cert.badgeLevel ?? cert.level).charAt(0).toUpperCase() + (cert.badgeType ?? cert.badgeLevel ?? cert.level).slice(1)} Certificate
              </span>
            )}
          </div>
        </motion.div>

        {/* Info grid */}
        <motion.div
          className="cd-info-card"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.22, delay: 0.08 }}
        >
          <h3 className="cd-info-title">Certificate Details</h3>
          <div className="cd-info-grid">

            {(cert.holderName ?? cert.userName ?? cert.fullName) && (
              <div className="cd-info-row">
                <div className="cd-info-icon-wrap">
                  <PersonIcon className="cd-info-icon" />
                </div>
                <div>
                  <div className="cd-info-label">Certificate Holder</div>
                  <div className="cd-info-value">{cert.holderName ?? cert.userName ?? cert.fullName}</div>
                </div>
              </div>
            )}

            {(cert.certificateNumber ?? cert.certNumber) && (
              <div className="cd-info-row">
                <div className="cd-info-icon-wrap">
                  <TagIcon className="cd-info-icon" />
                </div>
                <div>
                  <div className="cd-info-label">Certificate Number</div>
                  <div className="cd-info-value cd-mono">{cert.certificateNumber ?? cert.certNumber}</div>
                </div>
              </div>
            )}

            {(cert.badgeType ?? cert.badgeLevel ?? cert.level) && (
              <div className="cd-info-row">
                <div className="cd-info-icon-wrap" style={{ background: `${badgeColor}1a` }}>
                  <WorkspacePremiumIcon className="cd-info-icon" style={{ color: badgeColor }} />
                </div>
                <div>
                  <div className="cd-info-label">Badge Class</div>
                  <div className="cd-info-value" style={{ color: badgeColor }}>
                    {(cert.badgeType ?? cert.badgeLevel ?? cert.level).charAt(0).toUpperCase() + (cert.badgeType ?? cert.badgeLevel ?? cert.level).slice(1)}
                  </div>
                </div>
              </div>
            )}

            <div className="cd-info-row">
              <div className="cd-info-icon-wrap" style={{ background: 'rgba(75,227,207,0.12)' }}>
                <TagIcon className="cd-info-icon" style={{ color: '#4be3cf' }} />
              </div>
              <div>
                <div className="cd-info-label">Score</div>
                <div className="cd-info-value cd-score" style={{ color: '#4be3cf' }}>
                  {cert.score ?? '—'}%
                </div>
              </div>
            </div>

            <div className="cd-info-row">
              <div className="cd-info-icon-wrap">
                <CalendarTodayIcon className="cd-info-icon" />
              </div>
              <div>
                <div className="cd-info-label">Issue Date</div>
                <div className="cd-info-value">
                  {formatDate(cert.issuedOn ?? cert.issuedAt ?? cert.issueDate)}
                </div>
              </div>
            </div>

            {(cert.expiresOn ?? cert.expiresAt ?? cert.expiryDate) && (
              <div className="cd-info-row">
                <div className="cd-info-icon-wrap" style={isExpired ? { background: 'rgba(255,167,38,0.12)' } : {}}>
                  <CalendarTodayIcon className="cd-info-icon" style={isExpired ? { color: '#ffa726' } : {}} />
                </div>
                <div>
                  <div className="cd-info-label">Expiry Date</div>
                  <div className="cd-info-value" style={isExpired ? { color: '#ffa726' } : {}}>
                    {formatDate(cert.expiresOn ?? cert.expiresAt ?? cert.expiryDate)}
                    {isExpired && <span className="cd-expired-tag"> (Expired)</span>}
                  </div>
                </div>
              </div>
            )}

          </div>
        </motion.div>

        {/* Download button */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2, delay: 0.14 }}
        >
          <button
            className="cd-btn-download"
            onClick={handleDownload}
            disabled={downloading}
          >
            <DownloadIcon style={{ fontSize: 18 }} />
            {downloading ? 'Downloading…' : 'Download PDF Certificate'}
          </button>
        </motion.div>
      </div>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
